import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IStaffTask, TBucketMap, COLUMNS } from './taskBuckets';
import { TaskCard } from './TaskCard';
import * as S from './TaskBoard.styled';

interface ITaskColumnsProps {
  buckets: TBucketMap;
  onOpen: (task: IStaffTask) => void;
  onToggleDone: (task: IStaffTask) => void;
}

/**
 * The five always-visible date columns (overdue / today / tomorrow / this
 * week / later+no-date) plus, when closed tasks are fetched, a collapsible
 * «done» strip below the board.
 */
export const TaskColumns = memo(
  ({ buckets, onOpen, onToggleDone }: ITaskColumnsProps) => {
    const { t } = useTranslation();
    const [doneOpen, setDoneOpen] = useState(false);

    const renderCards = (tasks: IStaffTask[], overdue = false) =>
      tasks.map((task) => (
        <TaskCard
          key={task.id}
          task={task}
          overdue={overdue}
          onOpen={onOpen}
          onToggleDone={onToggleDone}
        />
      ));

    return (
      <>
        <S.Board>
          {COLUMNS.map(({ key, tone }) => {
            const tasks = buckets[key];
            const noDate = key === 'later' ? buckets.noDate : [];
            const count = tasks.length + noDate.length;
            return (
              <S.Column key={key}>
                <S.ColumnHead tone={tone}>
                  {t(`pages.dashboard.bucket.${key}`)}
                  <S.BucketCount>{count}</S.BucketCount>
                </S.ColumnHead>
                <S.ColumnBody>
                  {count === 0 ? (
                    <S.ColumnEmpty>
                      {t('pages.tasks.emptyColumn')}
                    </S.ColumnEmpty>
                  ) : (
                    <>
                      {renderCards(tasks, key === 'overdue')}
                      {noDate.length > 0 ? (
                        <>
                          <S.NoDateDivider>
                            {t('pages.dashboard.bucket.noDate')}
                          </S.NoDateDivider>
                          {renderCards(noDate)}
                        </>
                      ) : null}
                    </>
                  )}
                </S.ColumnBody>
              </S.Column>
            );
          })}
        </S.Board>

        {buckets.done.length > 0 ? (
          <S.DoneStrip>
            <S.DoneStripHead
              type="button"
              onClick={() => setDoneOpen((v) => !v)}
            >
              {doneOpen ? '▾' : '▸'} {t('pages.dashboard.bucket.done')}
              <S.BucketCount>{buckets.done.length}</S.BucketCount>
            </S.DoneStripHead>
            {doneOpen ? (
              <S.DoneStripBody>{renderCards(buckets.done)}</S.DoneStripBody>
            ) : null}
          </S.DoneStrip>
        ) : null}
      </>
    );
  },
);

TaskColumns.displayName = 'TaskColumns';
